/**
 * @fileoverview Drawer de detalhes dos destinatários de uma execução de disparo
 * em massa. Lista os contatos filtrados por status (falhas, lidos, entregues,
 * etc.) com telefone mascarado e o motivo — consumindo o endpoint já existente
 * GET /mass-send/executions/:id/recipients (paginado 50). Aberto ao clicar num
 * tile do MassSendMonitor.
 *
 * Props: { open, executionId, status, statusLabel, statusColor, onClose, fetchRecipients }
 */

import React from "react";
import { CircularProgress } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { X, Download } from "lucide-react";

const STATUS_COLORS = {
  pending: "#9ca3af",
  processing: "#60a5fa",
  sent: "#818cf8",
  delivered: "#34d399",
  read: "#22c55e",
  failed: "#f87171",
  ignored: "#fb923c",
  skipped: "#fb923c",
};

const STATUS_LABELS = {
  pending: "Pendente",
  processing: "Processando",
  sent: "Enviado",
  delivered: "Entregue",
  read: "Lido",
  failed: "Falha",
  ignored: "Ignorado",
  skipped: "Pulado",
};

// Limpa a mensagem de erro/motivo para exibição compacta (a 1ª linha carrega o
// essencial; o resto é dica formatada com emojis).
const shortReason = (r) => {
  const raw = r.errorMessage || r.skipReason || "";
  if (!raw) return "—";
  return String(raw).split("\n")[0].trim() || "—";
};

const fmt = (v) => {
  if (!v) return null;
  try {
    return new Date(v).toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch (e) {
    return null;
  }
};

const MassSendRecipientsDrawer = ({
  open,
  executionId,
  status,
  statusLabel,
  statusColor,
  onClose,
  fetchRecipients,
}) => {
  const isDark = useTheme().palette.type === "dark";
  const W = (a) => (isDark ? `rgba(255,255,255,${a})` : `rgba(20,22,30,${a})`);
  const bg = isDark ? "#1e2230" : "#ffffff";

  const [rows, setRows] = React.useState([]);
  const [page, setPage] = React.useState(1);
  const [count, setCount] = React.useState(0);
  const [hasMore, setHasMore] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  const load = React.useCallback(
    async (pageNumber) => {
      if (!executionId || !fetchRecipients) return;
      setLoading(true);
      setError(null);
      try {
        const data = await fetchRecipients(executionId, { status, pageNumber });
        const list = (data && data.recipients) || [];
        setCount((data && data.count) || 0);
        setHasMore(!!(data && data.hasMore));
        setRows((prev) => (pageNumber === 1 ? list : prev.concat(list)));
        setPage(pageNumber);
      } catch (e) {
        setError("Não foi possível carregar os detalhes.");
      } finally {
        setLoading(false);
      }
    },
    [executionId, status, fetchRecipients]
  );

  // (Re)carrega a 1ª página ao abrir ou trocar o filtro de status.
  React.useEffect(() => {
    if (open) {
      setRows([]);
      setPage(1);
      load(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, executionId, status]);

  if (!open) return null;

  const accent = statusColor || STATUS_COLORS[status] || W(0.6);
  const title = statusLabel || STATUS_LABELS[status] || "Todos";

  const chip = (st) => {
    const c = STATUS_COLORS[st] || W(0.5);
    return (
      <span
        style={{
          fontSize: 9, fontWeight: 700, color: c, background: `${c}22`,
          border: `1px solid ${c}55`, borderRadius: 6, padding: "1px 6px", whiteSpace: "nowrap",
        }}
      >
        {STATUS_LABELS[st] || st}
      </span>
    );
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 3000,
        background: "rgba(0,0,0,0.45)", display: "flex", justifyContent: "flex-end",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(460px, 96vw)", height: "100%", background: bg,
          borderLeft: "1px solid " + W(0.1), display: "flex", flexDirection: "column",
          boxShadow: "-8px 0 24px rgba(0,0,0,0.3)",
        }}
      >
        {/* Cabeçalho */}
        <div
          style={{
            display: "flex", alignItems: "center", gap: 8, padding: "14px 16px",
            borderBottom: "1px solid " + W(0.08),
          }}
        >
          <span style={{ width: 9, height: 9, borderRadius: "50%", background: accent }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: W(0.9) }}>
              {title}
            </div>
            <div style={{ fontSize: 10, color: W(0.45) }}>
              {count.toLocaleString("pt-BR")} contato{count === 1 ? "" : "s"}
            </div>
          </div>
          <div
            onClick={onClose}
            style={{ cursor: "pointer", color: W(0.5), display: "flex", padding: 4 }}
            title="Fechar"
          >
            <X size={18} />
          </div>
        </div>

        {/* Lista */}
        <div style={{ flex: 1, overflowY: "auto", padding: "6px 10px" }}>
          {error && (
            <div style={{ padding: 16, fontSize: 12, color: "#f87171", textAlign: "center" }}>
              {error}
            </div>
          )}
          {!error && !loading && rows.length === 0 && (
            <div style={{ padding: 24, fontSize: 12, color: W(0.45), textAlign: "center" }}>
              Nenhum contato neste filtro.
            </div>
          )}
          {rows.map((r) => {
            const reason = shortReason(r);
            const when = fmt(r.readAt) || fmt(r.deliveredAt) || fmt(r.sentAt);
            return (
              <div
                key={r.id}
                style={{
                  padding: "8px 8px", borderRadius: 8, marginBottom: 4,
                  background: W(0.02), border: "1px solid " + W(0.05),
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 12, fontWeight: 600, color: W(0.85),
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                      }}
                    >
                      {r.name || "—"}
                    </div>
                    <div style={{ fontSize: 10, color: W(0.5), fontVariantNumeric: "tabular-nums" }}>
                      {r.phoneMasked || "—"}
                    </div>
                  </div>
                  {chip(r.status)}
                </div>
                {(r.status === "failed" || r.status === "ignored" || r.status === "skipped") &&
                  reason !== "—" && (
                    <div style={{ fontSize: 10, color: W(0.55), marginTop: 4, lineHeight: 1.4 }}>
                      {r.errorKind === "permanent" ? "🚫 " : r.errorKind ? "↻ " : ""}
                      {reason}
                    </div>
                  )}
                {when && (
                  <div style={{ fontSize: 9, color: W(0.35), marginTop: 3 }}>{when}</div>
                )}
              </div>
            );
          })}
          {loading && (
            <div style={{ padding: 16, textAlign: "center" }}>
              <CircularProgress size={16} style={{ color: W(0.5) }} />
            </div>
          )}
        </div>

        {/* Rodapé: carregar mais */}
        {hasMore && !loading && (
          <div style={{ padding: "10px 12px", borderTop: "1px solid " + W(0.08) }}>
            <div
              onClick={() => load(page + 1)}
              style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                padding: "8px 0", borderRadius: 8, cursor: "pointer",
                background: W(0.04), border: "1px solid " + W(0.12),
                color: W(0.7), fontSize: 11, fontWeight: 600,
              }}
            >
              <Download size={13} /> Carregar mais
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MassSendRecipientsDrawer;
